import { Routes } from '@angular/router';

import { AuthGuard, MerchantAuthGuard } from './_guards/auth.guard';

import { ReverseAuthGuard, ReverseMerchantAuthGuard } from './_guards/reverse-auth.guard';
import { UserManagementGuard, AdministrationGuard } from './_guards/user.guard';
import { CanDeactivateGuard } from './_services/can-deactivate-guard.service';

import { LightShowGuard, CouponGuard, SelfieCamGuard, TriviaGuard, CoreEngineGuard, UpnGuard, Cue5050Guard, PledgePerWinGuard } from './_guards/service.guard';

import { LicensingComponent } from './licensing/licensing.component';
import { RegisterComponent } from './register/register.component';
import { LoginComponent } from './login/login.component';
import { LightShowCreateComponent } from './light-show-management/light-show-create.component';
import { PasswordResetComponent } from './password-reset/password-reset.component';
import { UserManagementComponent } from './user-management/user-management.component';
import { LightShowManagementComponent } from './light-show-management/light-show-management.component';
import { UserCreateComponent } from './user-management/user-create.component';
import { TrackLibraryComponent } from './songs/track-library/track-library.component';
import { PortalComponent } from './portal/portal.component';
import { SelfieCamComponent } from './selfie-cam/selfie-cam.component';
import { TriviaComponent } from './trivia/trivia.component';
import { SelfieCamManagementComponent } from './selfie-cam/selfie-cam-management.component';
import { TriviaManagementComponent } from './trivia/trivia-management.component';
import { HelpComponent } from './help-page/help.component';
import { ToolkitComponent } from './toolkit-page/toolkit.component';
import { AdministrationComponent } from './administration/admin.component';
import { PaymentHistoryComponent } from './administration/payment-history.component';
import { EngineManagementComponent } from './administration/engine-management.component';
import { CouponsComponent } from './coupons/coupons.component';
import { CouponFormComponent } from './coupons/coupon-form/coupon-form.component';
import { UpnComponent } from './upn/upn.component';
import { UpnFormComponent } from './upn/upn-form/upn-form.component';
import { CoreEngineManagementComponent } from './core-engine/core-engine.component';
import { NotFoundComponent } from './_shared/not-found.component';
import { OrganizationsComponent } from './administration/organizations/organizations.component';
import { OrganizationsFormComponent } from './administration/organizations/organizations-form/organizations-form.component';
import { SelfieCamViewComponent } from './selfie-cam/selfie-cam-view.component';
import { CoreEngineFormComponent } from './core-engine/core-engine-form/core-engine.component';
import { ClientWizardComponent } from './administration/client-wizard/client-wizard.component';
import { TriviaPlayersComponent } from './trivia/trivia-results/trivia-players.component';
import { TriviaResultsComponent } from './trivia/trivia-results/trivia-results.component';
import { MobileAppStoreRedirectComponent } from './mobile-app-store-redirect/mobile-app-store-redirect.component';
import { VenueManagementComponent } from './venue-editor/mangement/venue-mangement.component';
import { VenueFormComponent } from './venue-editor/form/venue-form.component';
import { ConfigManagementComponent } from './administration/engine-config-management.component';
import { ScheduledEventComponent } from './scheduled-events/scheduled-events.component';
import { FundraisingManagementComponent } from './fundraising/fundraising-management.component';
import { ImageUploadManagementComponent } from './administration/image-upload-management.component';

import { MerchantManagementComponent } from './fundraising/admin/merchant/merchant-management.component';
import { MerchantCreateComponent } from './fundraising/admin/merchant/merchant-create.component';
import { MerchantLoginComponent } from './fundraising/merchant/merchant-login.component';
import { MerchantPortalComponent } from './fundraising/merchant/merchant-portal.component';

import { VendorManagementComponent } from './fundraising/admin/vendor/vendor-management.component';
import { VendorCreateComponent } from './fundraising/admin/vendor/vendor-create.component';
import { Cue5050CreateComponent } from './fundraising/cue5050/cue5050-create.component';
import { StationManagementComponent } from './fundraising/station/station-management.component';
import { OperatorManagementComponent } from './fundraising/operator/operator-management.component';
import { LocationManagementComponent } from './fundraising/location/location-management.component';
import { LocationCreateComponent } from './fundraising/location/location-create.component';
import { OperatorCreateComponent } from './fundraising/operator/operator-create.component';
import { StationCreateComponent } from './fundraising/station/station-create.component';
import { VendorWebsiteCreateComponent } from './fundraising/website/vendor-website-create.component';
import { EventPotSizeComponentComponent } from './fundraising/event-pot-size-component/event-pot-size-component.component';
import { TicketHistoryManagementComponent } from './fundraising/ticket-history-management/ticket-history-management.component';
import { StationAppManagementComponent } from './fundraising/admin/station-app/station-app-management.component';
import { PledgePerWinManagementComponent } from './fundraising/ppw/ppw-management.component';
import { PledgePerWinCreateComponent } from './fundraising/ppw/ppw-create.component';
import { ReportsLandingPageComponent } from './fundraising/reports/reports-landing-page.component';
import { BarGraphReportComponent } from './fundraising/reports/bar-graph-report.component';
import { SavedReportsComponent } from './fundraising/reports/saved-reports.component';
import { TeamManagementComponent } from './fundraising/ppw/teams/team-management.component';
import { TeamCreateComponent } from './fundraising/ppw/teams/team-create.component';
import { PrinterManagementComponent } from './fundraising/printers/printer-management.component';
import { PrinterCreateComponent } from './fundraising/printers/printer-create.component';
import { PledgePerWinGameManagementComponent } from './fundraising/ppw/games/ppw-game-management.component';
import { PledgePerWinSubscribersManagementComponent } from './fundraising/ppw/subscribers/ppw-subscribers-management.component';
import { PledgePerWinWebsiteCreateComponent } from './fundraising/ppw/website/ppw-website-create.component';
import { VendorUserCreateComponent } from './fundraising/admin/vendor-user/vendor-user-create.component';
import { PledgePerWinPayoutManagementComponent } from './fundraising/ppw/ppw-payout-management.component';
import { AppConfigManagementComponent } from './administration/app-config-management.component';
import { LightShowPublicPreviewComponent } from './lightshow-public-preview/lightshow-public-preview.component';

export const AppRoutes: Routes = [
  { path: '', component: LoginComponent },
  {
    path: 'preview/:guid',
    component: LightShowPublicPreviewComponent
  },
  {
    path: 'portal',
    runGuardsAndResolvers: 'always',
    component: PortalComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', redirectTo: 'portal', pathMatch: 'full' },
      { path: 'licensing', component: LicensingComponent },
      { path: 'help', component: HelpComponent },
      { path: 'toolkit', component: ToolkitComponent },
      {
        path: 'track-library',
        component: TrackLibraryComponent
      },
      {
        path: 'trivia',
        runGuardsAndResolvers: 'always',
        component: TriviaManagementComponent,
        canActivate: [TriviaGuard]
      },
      {
        path: 'trivia/create',
        component: TriviaComponent,
        runGuardsAndResolvers: 'always',
        canDeactivate: [CanDeactivateGuard],
        canActivate: [TriviaGuard]
      },
      {
        path: 'trivia/:serviceId/:id',
        component: TriviaComponent,
        runGuardsAndResolvers: 'always',
        canDeactivate: [CanDeactivateGuard],
        canActivate: [TriviaGuard]
      },
      {
        path: 'selfie-cam',
        runGuardsAndResolvers: 'always',
        component: SelfieCamManagementComponent,
        canActivate: [SelfieCamGuard]
      },
      {
        path: 'selfie-cam/create',
        component: SelfieCamComponent,
        runGuardsAndResolvers: 'always',
        canDeactivate: [CanDeactivateGuard],
        canActivate: [SelfieCamGuard]
      },
      {
        path: 'selfie-cam/:serviceId/:id',
        component: SelfieCamComponent,
        runGuardsAndResolvers: 'always',
        canDeactivate: [CanDeactivateGuard],
        canActivate: [SelfieCamGuard]
      },
      {
        path: 'selfie-cam/:serviceId/:id/view',
        component: SelfieCamViewComponent,
        runGuardsAndResolvers: 'always',
        canDeactivate: [CanDeactivateGuard],
        canActivate: [SelfieCamGuard]
      },
      {
        path: 'selfie-cam/:id/:duplicate',
        component: SelfieCamComponent,
        runGuardsAndResolvers: 'always',
        canDeactivate: [CanDeactivateGuard],
        canActivate: [SelfieCamGuard]
      },
      {
        path: 'light-show',
        runGuardsAndResolvers: 'always',
        component: LightShowManagementComponent,
        canActivate: [LightShowGuard]
      },
      {
        path: 'light-show/create',
        component: LightShowCreateComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [LightShowGuard],
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: 'light-show/:serviceId/:id',
        component: LightShowCreateComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [LightShowGuard],
        canDeactivate: [CanDeactivateGuard]
      },
      {
        path: 'scheduled-events',
        runGuardsAndResolvers: 'always',
        component: ScheduledEventComponent,
      },
      {
        path: 'audio-qr',
        runGuardsAndResolvers: 'always',
        component: CoreEngineManagementComponent,
        canActivate: [CoreEngineGuard]
      },
      {
        path: 'audio-qr/create',
        component: CoreEngineFormComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [CoreEngineGuard],
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: 'audio-qr/create/:id',
        component: CoreEngineFormComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [CoreEngineGuard],
        canDeactivate: [CanDeactivateGuard]
      },
      {
        path: 'users',
        runGuardsAndResolvers: 'always',
        canActivate: [UserManagementGuard],
        component: UserManagementComponent
      },
      {
        path: 'users/create',
        component: UserCreateComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [UserManagementGuard],
        canDeactivate: [CanDeactivateGuard]
      },
      {
        path: 'users/:id',
        component: UserCreateComponent,
        runGuardsAndResolvers: 'always',
        canActivate: [UserManagementGuard],
        canDeactivate: [CanDeactivateGuard]
      },
      {
        path: 'admin',
        redirectTo: 'admin/client-type',
        pathMatch: 'full'
      },
      {
        path: 'admin/client-type',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: AdministrationComponent
      },
      {
        path: 'admin/payment-history',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: PaymentHistoryComponent
      },
      {
        path: 'admin/engine',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: EngineManagementComponent
      },
      {
        path: 'admin/engine-configs',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: ConfigManagementComponent
      },
      {
        path: 'admin/app-configs',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: AppConfigManagementComponent
      },
      {
        path: 'admin/organization',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: OrganizationsComponent
      },
      {
        path: 'admin/organization/create',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: OrganizationsFormComponent
      },
      {
        path: 'admin/organization/:id',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: OrganizationsFormComponent
      },
      {
        path: 'admin/client-wizard',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        canDeactivate: [CanDeactivateGuard],
        component: ClientWizardComponent
      },
      {
        path: 'fundraising-admin',
        children: [
          {
            path: '',
            redirectTo: 'vendor',
            pathMatch: 'full'
          },
          {
            path: 'vendor',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: VendorManagementComponent
          },
          {
            path: 'vendor/create',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: VendorCreateComponent
          },
          {
            path: 'vendor/create-user',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: VendorUserCreateComponent
          },
          {
            path: 'vendor/create/:id',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: VendorCreateComponent
          },
          {
            path: 'station-app',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: StationAppManagementComponent
          },
          {
            path: 'merchant',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: MerchantManagementComponent
          },
          {
            path: 'merchant/create',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: MerchantCreateComponent
          },
          {
            path: 'merchant/create/:id',
            runGuardsAndResolvers: 'always',
            canActivate: [AdministrationGuard],
            canDeactivate: [CanDeactivateGuard],
            component: MerchantCreateComponent
          },
        ]
      },
      {
        path: 'admin/images',
        runGuardsAndResolvers: 'always',
        canActivate: [AdministrationGuard],
        component: ImageUploadManagementComponent
      },
      {
        path: 'coupons',
        runGuardsAndResolvers: 'always',
        canActivate: [CouponGuard],
        canDeactivate: [CanDeactivateGuard],
        component: CouponsComponent
      },
      {
        path: 'upn',
        runGuardsAndResolvers: 'always',
        canActivate: [UpnGuard],
        canDeactivate: [CanDeactivateGuard],
        component: UpnComponent
      },
      {
        path: 'coupons/create',
        runGuardsAndResolvers: 'always',
        canActivate: [CouponGuard],
        canDeactivate: [CanDeactivateGuard],
        component: CouponFormComponent
      },
      {
        path: 'upn/create',
        runGuardsAndResolvers: 'always',
        canActivate: [UpnGuard],
        canDeactivate: [CanDeactivateGuard],
        component: UpnFormComponent
      },
      {
        path: 'coupons/:id',
        runGuardsAndResolvers: 'always',
        canActivate: [CouponGuard],
        canDeactivate: [CanDeactivateGuard],
        component: CouponFormComponent
      },
      {
        path: 'upn/:serviceId/:id',
        runGuardsAndResolvers: 'always',
        canActivate: [UpnGuard],
        canDeactivate: [CanDeactivateGuard],
        component: UpnFormComponent
      },
      {
        path: 'trivia/results/:serviceId/:triviaId',
        runGuardsAndResolvers: 'always',
        canActivate: [TriviaGuard],
        canDeactivate: [CanDeactivateGuard],
        component: TriviaResultsComponent
      },
      {
        path: 'trivia/results/:serviceId/:triviaId/:serviceLogId',
        runGuardsAndResolvers: 'always',
        canActivate: [TriviaGuard],
        canDeactivate: [CanDeactivateGuard],
        component: TriviaPlayersComponent
      },
      {
        path: 'venue',
        runGuardsAndResolvers: 'always',
        canActivate: [LightShowGuard],
        canDeactivate: [CanDeactivateGuard],
        component: VenueManagementComponent
      },
      {
        path: 'venue/create/:id',
        runGuardsAndResolvers: 'always',
        canActivate: [LightShowGuard],
        canDeactivate: [CanDeactivateGuard],
        component: VenueFormComponent
      },
      {
        path: 'venue/create',
        runGuardsAndResolvers: 'always',
        canActivate: [LightShowGuard],
        canDeactivate: [CanDeactivateGuard],
        component: VenueFormComponent
      },
      {
        path: 'fundraising/cue5050',
        children: [
          {
            path: '',
            redirectTo: 'event',
            pathMatch: 'full'
          },
          {
            path: 'event',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: FundraisingManagementComponent
          },
          {
            path: 'event/cue5050',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: Cue5050CreateComponent
          },
          {
            path: 'event/cue5050/:id',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: Cue5050CreateComponent
          },
          {
            path: 'station',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: StationManagementComponent
          },
          {
            path: 'station/update/:id',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: StationCreateComponent
          },
          {
            path: 'station/create',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: StationCreateComponent
          },
          {
            path: 'operator',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: OperatorManagementComponent
          },
          {
            path: 'operator/update/:id',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: OperatorCreateComponent
          },
          {
            path: 'operator/create',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: OperatorCreateComponent
          },
          {
            path: 'location',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: LocationManagementComponent
          },
          {
            path: 'location/update/:id',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: LocationCreateComponent
          },
          {
            path: 'location/create',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: LocationCreateComponent
          },      
          {
            path: 'website',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: VendorWebsiteCreateComponent
          },
          {
            path: 'tickets',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: TicketHistoryManagementComponent
          },
          {
            path: 'reports',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: ReportsLandingPageComponent
          },
          {
            path: 'reports/bar-graph',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: BarGraphReportComponent
          },
          {
            path: 'reports/saved',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: SavedReportsComponent
          },
          {
            path: 'reports/saved/view',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: BarGraphReportComponent
          },
          {
            path: 'printers',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: PrinterManagementComponent
          },
          {
            path: 'printers/create',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: PrinterCreateComponent
          },
          {
            path: 'printers/update/:id',
            runGuardsAndResolvers: 'always',
            canActivate: [Cue5050Guard],
            canDeactivate: [CanDeactivateGuard],
            component: PrinterCreateComponent
          },
        ]
      },
      {
        path: 'fundraising/ppw',
        canActivate: [PledgePerWinGuard],
        children: [
          {
            path: '',
            redirectTo: 'website',
            pathMatch: 'full'
          },
          {
            path: 'event',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: PledgePerWinManagementComponent
          },
          {
            path: 'event/create',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: PledgePerWinCreateComponent
          },
          {
            path: 'event/update/:id',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: PledgePerWinCreateComponent
          },
          {
            path: 'games',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: PledgePerWinGameManagementComponent
          },
          {
            path: 'team',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: TeamManagementComponent
          },
          {
            path: 'team/create',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: TeamCreateComponent
          },
          {
            path: 'team/edit/:id',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: TeamCreateComponent
          },
          {
            path: 'subscribers',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: PledgePerWinSubscribersManagementComponent
          },
          {
            path: 'website',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: PledgePerWinWebsiteCreateComponent
          },
          {
            path: 'payouts',
            runGuardsAndResolvers: 'always',
            canDeactivate: [CanDeactivateGuard],
            component: PledgePerWinPayoutManagementComponent
          }
        ]
      }
    ]
  },
  { path: 'app/:id', component: MobileAppStoreRedirectComponent },
  { path: 'home', component: LightShowManagementComponent },
  { path: 'register', component: RegisterComponent },
  { path: 'login', component: LoginComponent, canActivate: [ReverseAuthGuard] },
  { path: 'pot/:theme/:id', component: EventPotSizeComponentComponent },

  { path: 'merchant-login', component: MerchantLoginComponent, canActivate: [ReverseMerchantAuthGuard] },
  {
    path: 'merchant',
    runGuardsAndResolvers: 'always',
    component: MerchantPortalComponent,
    canActivate: [MerchantAuthGuard],
    children: [
      { path: '', redirectTo: 'merchant', pathMatch: 'full' },
    ] 
  },
  { path: '404', component: NotFoundComponent },
  //{ path: '**', redirectTo: '404' }
  
];

